import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatSort, Sort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { I18NextPipe } from 'angular-i18next';
import { Store } from '@ngrx/store';
import * as mainActions from 'src/app/state/app.actions';
import { ErrorMessage, InfoMessage } from 'src/app/common/messages';
import { mainState } from 'src/app/state/app.reducer';
import { DialogConfirmComponent } from 'src/app/common/dialogs/dialog-confirm/dialog-confirm.component';
import { DialogDeleteNewComponent } from 'src/app/common/dialogs/dialog-delete-new/dialog-delete-new.component';
import { DialogHelpComponent } from 'src/app/common/dialogs/dialog-help/dialog-help.component';
import { FileUploadDialogComponent, FileUploadColumn } from 'src/app/shared/dialogs/file-upload-dialog/file-upload-dialog.component';
import { GetFilterUserListRequest, UpdateUserNameAndGroupItem, UsersUsersService } from 'src/app/services/services/users-users.service';
import { finalize } from 'rxjs/operators';

export interface FilterMenuItem {
  id: number;
  description: string;
}

@Component({
  selector: 'app-users',
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.scss'],
  standalone: false
})
export class UsersComponent implements OnInit, OnDestroy {

  filterMenuItems: FilterMenuItem[] = [];
  private routeParamsSub: Subscription;

  constructor(
    private dialog: MatDialog,
    private i18n: I18NextPipe,
    private usersUsersService: UsersUsersService,
    private router: Router,
    private route: ActivatedRoute,
    private store: Store<mainState>
  ) { }

  BreadcrumbsLink = [
    { label: 'Home', url: '/' },
    { label: 'Users', url: '/users/users' }
  ];

  skillLevels = Array.from({ length: 10 }, (_, i) => ({ value: i, label: i.toString(), }));
  showNumericId = false;
  showKeywords = false;
  showExternalUserId = false;
  showLastSignIn = false;
  sortOrder = 'first';
  unblockableColumns: string[] = ['userName','num','delete'];

  get displayedColumns(): string[] {
    const columns = ['name', 'email', 'mobile', 'filter', 'type'];
    if (this.showNumericId) { columns.push('Numeric ID');}
    columns.push('user group', 'username');
    if (this.showKeywords) { columns.push('Keywords');}
    if (this.showExternalUserId) { columns.push('External User ID');}
    columns.push('sync', 'puzzel ID');
    if (this.showLastSignIn) { columns.push('Last sign-in');}
    columns.push('delete');
    return columns;
  }
  filterText = '';
  indexData: any = null;
  filteredUsers: any[] = [];
  userGroupOptions: any[] = [];
  profileOptions: any[] = [];
  skillOptions: any[] = [];
  unblockableUsersDataSource = new MatTableDataSource<any>([]);
  selectedUserGroupId: number | null = null;
  selectedProfileId: number | null = null;
  selectedSkillId: number | null = null;
  selectedLevels: number[] = [];

  @ViewChild('unblockableSort') set unblockableSortSetter(sort: MatSort) {
    if (sort) {
      this.unblockableUsersDataSource.sort = sort;
    }
  }

  sortColumn: string | null = null;
  sortDirection: 'asc' | 'desc' | null = null;
  isLoading = false;
  ddlLevelDisabled = true;
  oneIdentitySynched : boolean = false;
  canEditUsers : boolean = false;
  canEditMyself : boolean = false;
  canAddUsers : boolean = false;
  currentUserId: number | null = null;

  ngOnInit(): void {
    this.routeParamsSub = this.route.params.subscribe((params) => {
      this.selectedUserGroupId = params['id'] ? +params['id'] : null;
      this.loadData();
    });
  }

  ngOnDestroy(): void {
    this.routeParamsSub?.unsubscribe();
  }

  pageSize: number = 25;
  datasetLength: number = 0;
  pageNum: number = 1;

  onPageChange($event) {
    console.log('Page changed:', $event);
    const nextPageNum = ($event?.pageIndex ?? 0) + 1;
    const nextPageSize = $event?.pageSize ?? this.pageSize;
    if (nextPageNum === this.pageNum && nextPageSize === this.pageSize) {
      return;
    }
    this.pageNum = nextPageNum;
    this.pageSize = nextPageSize;
    this.loadUserList();
  }

  onPageSizeChange(newSize: number): void {
    if (!Number.isFinite(newSize) || newSize <= 0) {
      return;
    }

    if (newSize === this.pageSize) {
      return;
    }

    this.pageSize = newSize;
    this.pageNum = 1;
    this.loadUserList();
  }

  onSortChange(sort: Sort) {
    this.sortColumn = sort?.direction ? sort.active : null;
    this.sortDirection = sort?.direction ? (sort.direction as 'asc' | 'desc') : null;
    this.pageNum = 1;
    this.loadUserList();
  }

  onFilterChange(): void {
    this.pageNum = 1;
    this.loadUserList();
  }

  onFilterInputChange(value: string): void {
    if (value === '') {
      this.onFilterChange();
    }
  }

  onSkillChange() {
    if (this.selectedSkillId) {
      this.ddlLevelDisabled = false;
    } else {
      this.ddlLevelDisabled = true;
      this.selectedLevels = [];
    }
    this.onFilterChange();
  }

  formatKeywords(keywords: string): string {
    if (!keywords) {
      return '-';
    }
    return keywords?.replace(/§/g, ", ");
  }

  private loadData(): void {
    this.usersUsersService.getIndex().subscribe((response) => {
      this.indexData = response;
      if (!response?.skillLevels || !response?.skillLevels.length) {
        this.skillLevels = response?.skillLevels.map((i) => ({ value: i, label: i.toString(), }));
      }
      const userGroups = response?.userGroupList || response?.UserGroupList || [];
      const profiles = response?.profiles || response?.profiles || [];
      const skills = response?.userSkillElements || response?.UserSkillElements || [];
      const allLabel = this.i18n.transform('users:users_allUserGroups') || 'All';
      const emptySkillOption = { id: null, name: allLabel };
      const emptyProfileOption = { profileId: null, profileDescript: allLabel };
      const emptyUserGroupOption = { id: null, name: allLabel };

      this.userGroupOptions = [emptyUserGroupOption, ...userGroups];
      this.profileOptions = [emptyProfileOption, ...profiles];
      this.skillOptions = [emptySkillOption, ...skills];
      this.oneIdentitySynched = response?.oneIdentitySynched ?? false;
      this.canEditUsers = response?.canEditUsers ?? false;
      this.canEditMyself = response?.canEditMyself ?? false;
      this.canAddUsers = response?.canAddUsers ?? false;
      this.currentUserId = response?.self?.id ?? null;

      if (this.indexData?.showUnblockable) {
        this.loadUnblockable();
      }
    });

    this.loadUserList();
  }

  private loadUserList(): void {
    this.isLoading = true;
    const request: GetFilterUserListRequest = {
      profileId: this.selectedProfileId,
      qualityElementId: this.selectedSkillId,
      qualityLevels: this.selectedLevels,
      searchText: this.filterText,
      sortColumn: this.sortColumn,
      sortDirection: this.sortDirection,
      pageSize: this.pageSize,
      pageNum: this.pageNum,
      userGroupId: this.selectedUserGroupId

    };

    this.usersUsersService
      .getFilterUserList(request)
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe((response) => {
        this.filteredUsers = response?.users || [];
        this.datasetLength = response?.totalCount || 0;
      });
  }

  private loadUnblockable(): void {
    this.usersUsersService.getUnblockableUserList().subscribe((response) => {
      this.unblockableUsersDataSource.data = response || [];
    });
  }

  getFilterItems(filterStr: string): FilterMenuItem[] {
    if (!filterStr || typeof filterStr !== 'string') {
      return [];
    }
    try {
      const arr = JSON.parse(filterStr);
      return Array.isArray(arr)
        ? arr.filter((x: any) => x && typeof x.id === 'number' && x.description)
        : [];
    } catch {
      return [];
    }
  }

  getFilterTooltip(filterStr: string): string {
    const items = this.getFilterItems(filterStr);
    if (!items.length) {
      return '';
    }
    return items.map(i => i.description).join(', ');
  }

  setFilterMenuRow(r: { filter?: string }): void {
    this.filterMenuItems = this.getFilterItems(r.filter || '');
  }

  navigateToFilter(id: number): void {
    this.router.navigate(['/users/users-filter/get', id]);
  }

  filterMenuOpen(r: { filter?: string }): void {
    this.setFilterMenuRow(r);
  }


  generateFullName(user): string {

    if (user.firstName === null && user.lastName === null)
      return '(' + user.userName + ')' || '(' + user.num + ')';
    if ((user.firstName == null || user.firstName?.trim() == '') && (user.lastName == null || user.lastName?.trim() == ''))
      return '(' + user.userName + ')' || '(' + user.num + ')';
    if (user.firstName === null || user.firstName?.trim() == '')
      return user.lastName;
    if (user.lastName === null || user.lastName?.trim() == '')
      return user.firstName;
    return this.sortOrder == 'first' ? user.firstName + ' ' + user.lastName :
      user.lastName + ' ' + user.firstName;
  }

  canEditUser(r: any): boolean {
    if (!this.canEditMyself && r.id === this.currentUserId) {
      return false;
    }
    return this.canEditUsers;
  }

  deleteDisabled(r: any): boolean {
    if(!this.canEditMyself && r.id === this.currentUserId){
      return true;
    }
    return this.canEditUsers ? false : true;
  }

  exportUserList(): void {
    this.usersUsersService.downloadUserData(this.selectedUserGroupId).subscribe((blob: Blob) => {
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'user-data.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    });
  }

  openImportDialog(): void {
    const dataF = this.i18n.transform('users:users_err_dataformat');
    const uNoExist = this.i18n.transform('users:users_err_notexistdata');
    const uExist = this.i18n.transform('users:users_err_existdata');
    const columns: FileUploadColumn[] = [
      { name: 'user_name', required: true, validationRegex: /[\\/"|^£$%¤§]/g, validationMessage: dataF, serverValidationMessage: uNoExist },
      { name: 'user_num', required: false, validationRegex: /^$|^\d{1,6}$/, validationMessage: dataF, serverValidationMessage: uExist },
      { name: 'keywords', required: false, validationRegex: /[\\/"|^£$%¤]/g, validationMessage: dataF }
    ];

    const dialogRef = this.dialog.open(FileUploadDialogComponent, {
      width: '820px',
      maxHeight: '90vh',
      data: {
        columns,
        title: this.i18n.transform('users:user_importUserSimpleUpdate'),
        serverValidateFn: (fData: string, fileName: string) => this.usersUsersService.validateSimpleUpdateUserData(fData, fileName),
        importFn: (fData: string, fileName: string, colsInFile: string) => this.usersUsersService.importUserSimpleUpdateList(fData,fileName,colsInFile)
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.loadUserList();
        this.loadUnblockable();
      }
    });
  }

  deleteUser(user: any): void {
    const data: UpdateUserNameAndGroupItem[] = [{ userId: user.id, deleteUser: true, deleteGroupNumber: false }];
    this.usersUsersService.deleteUsersCheck(data).subscribe((response) => {
      const error = response?.returnObject?.[0]?.errorName;
      if (error) {
        if (error === 'UserIsActive') {
          this.store.dispatch(
            mainActions.addMessage({
              message: new ErrorMessage('User is active', this.i18n.transform("users:users_lang_userIsActiveAndWillNotDelete"))
            })
          );
        }
        if (error === 'UserHasNoOtherPuzzelProducts') {
          const dialogRef = this.dialog.open(DialogConfirmComponent, {
            disableClose: true,
            width: '620px',
            data: {
              message: this.i18n.transform('users:users_lang_userHasNoPuzzelIdSolutions'),
              showCancel: true
            }
          });
          dialogRef.afterClosed().subscribe(result => {
            const data: UpdateUserNameAndGroupItem[] = [{ userId: user.id, deleteUser: true, deleteGroupNumber: false }];

            if (result === 'yes' || result === 'no' ) {
              const deletePuzzelId = result === 'yes';
              this.postToDeleteUsers(data, deletePuzzelId, user.userName, false);
            }
          });
        }
      } else {
        const dialogRef = this.dialog.open(DialogDeleteNewComponent, {
          disableClose: true,
          data: {
            title: this.i18n.transform('users:shared_deleteConfirm'),
            additionalText: "Are you sure you want to delete user " + user.userName + "?",
            showTitleInHeader: true,
            confirmText: this.i18n.transform('Delete'),
            cancelText: this.i18n.transform('Cancel'),
          }
        });
        dialogRef.afterClosed().subscribe(confirm => {
          if (!confirm) return;
          const data: UpdateUserNameAndGroupItem[] = [{ userId: user.id, deleteUser: true, deleteGroupNumber: false }];
          this.postToDeleteUsers(data, false, user.userName, false);
        });
      }
    });
  }

  deleteGroupNumber(groupNumber: any): void {
    const dialogRef = this.dialog.open(DialogDeleteNewComponent, {
      disableClose: true,
      data: {
        title: this.i18n.transform('users:shared_deleteConfirm'),
        additionalText: "Are you sure you want to delete group number " + groupNumber.userName + "?",
        showTitleInHeader: true,
        confirmText: this.i18n.transform('Delete'),
        cancelText: this.i18n.transform('Cancel'),
      }
    });
    dialogRef.afterClosed().subscribe(confirm => {
      if (!confirm) return;
      const data: UpdateUserNameAndGroupItem[] = [{ userId: groupNumber.id, groupNumberId: groupNumber.id, deleteUser: false, deleteGroupNumber: true }];
      this.postToDeleteUsers(data, false, groupNumber.userName, true);
    });
  }

  postToDeleteUsers(data: UpdateUserNameAndGroupItem[], deletePuzzelId: boolean, userName: string, isGroupNumber: boolean): void {
    this.usersUsersService.updateUsers(data, deletePuzzelId).subscribe({
      next: (response) => {
        if (!response) {
          response = { ok: false };
        }
        if (response?.ok) {
          this.store.dispatch(mainActions.addMessage({ message: new InfoMessage(this.i18n.transform('users:users_err_updateUsersSuccess'), userName) }));
          this.loadUserList();
          if(isGroupNumber){
            this.loadUnblockable();
          }
        } else {
          if (!response?.errorMessage) response = { ok: false, errorMessage: 'shared_err_postError' };
          this.store.dispatch(mainActions.addMessage({ message: new ErrorMessage(this.i18n.transform('users:' + response?.errorMessage).replace('{0}', userName), '') }));
        }
      },
      error: () => {
        this.store.dispatch(mainActions.addMessage({ message: new ErrorMessage(this.i18n.transform('users:shared_err_postError'), '') }));
      }
    });
  };



  openHelp(): void {
    this.dialog.open(DialogHelpComponent, {
      panelClass: 'dialog-help-pannel',
      backdropClass: 'backdrop-invisible',
      width: '500px',
      data: {
        title: this.i18n.transform('users:help_title_users'),
        text: this.i18n.transform('users:help_users'),
        renderAsHtml: true
      }
    });
  }

}
